'use client'

import { Configuration, LogLevel } from '@azure/msal-browser'

import { POPUP_RELAY_PATH } from './rutasPuente'

import { env } from '@/core/config/env'

// API_SCOPE depende de un valor RUNTIME (env.API_SCOPE viene de /api/config), por eso
// es una función que se evalúa al llamarla — NO una constante de módulo (que se evaluaría
// en import time, antes de hidratar el config).
const getApiScope = (): string => `api://${env.API_SCOPE}/.default`

const loggerCallback = (level: LogLevel, message: string, containsPii: boolean): void => {
  if (containsPii) {
    return
  }
  if (!env.IS_PRODUCTION) {
    switch (level) {
      case LogLevel.Error:
        console.error('msal-Error->', message)

        return

      case LogLevel.Info:
        return

      case LogLevel.Verbose:
        return

      case LogLevel.Warning:
        console.warn('msal-Warning->', message)

        return
    }
  }
}

const system = {
  loggerOptions: {
    loggerCallback,
    logLevel: LogLevel.Verbose,
    piiLoginEnabled: false,
  },
}

// buildMsalConfig() se llama de forma diferida (después de hidratar el runtime config),
// por eso lee env.CLIENT_ID en el momento de la llamada y NO en import time.
export const buildMsalConfig = (): Configuration => {
  if (!env.CLIENT_ID) {
    throw new Error('El CLIENT_ID no está definido (runtime config no hidratado o Application Setting faltante)')
  }

  return {
    auth: {
      clientId: env.CLIENT_ID,
      authority: 'https://login.microsoftonline.com/2a967794-c4d1-4bc2-8036-1a5201a5d767',
      redirectUri: '/redirect',
      postLogoutRedirectUri: '/autenticacion/ingreso',
      // La app también corre embebida en Salesforce (iframe de otro sitio). Ahí el popup normal de
      // v5 no vuelve: /redirect manda la respuesta por BroadcastChannel y el navegador lo particiona
      // por sitio superior, así que al iframe nunca le llega, loginPopup muere por timeout y el
      // segundo clic da interaction_in_progress. Con el relevo, MSAL abre primero esta página propia
      // de nivel superior, que abre el popup de Entra y devuelve la respuesta al iframe por
      // postMessage. Solo interviene en los flujos popup (login y logout embebidos); el redirect de
      // la pestaña propia no cambia. Ver src/app/popup-relay/page.tsx.
      popupRelayUri: POPUP_RELAY_PATH,
    },
    cache: {
      // localStorage: la sesión es compartida entre pestañas, que es lo que el usuario espera.
      // Con sessionStorage cada pestaña tenía su propia sesión y una pedía login mientras la
      // otra seguía dentro. El riesgo que sessionStorage evitaba (una pestaña vieja con token
      // vivo tras cerrar sesión) ya lo cubre el logout real: logoutRedirect limpia este cache
      // y BroadcastChannel avisa al resto de pestañas.
      cacheLocation: 'localStorage',
    },
    system,
  }
}

export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
}

/**
 * Scopes OIDC que acompañan SIEMPRE al `.default` de la API.
 *
 * Sin ellos, la respuesta del refresh **no trae `id_token`** y MSAL conserva el del cache. Y la
 * cookie que valida el proxy ES el ID token, así que al vencer la sesión se cae.
 *
 * Lo que hace falta entender es por qué eso cerraba sesión mucho antes de la hora: **cualquier**
 * llamada silenciosa puede ser la que canjee el refresh token. Si la primera en hacerlo es una
 * de las que piden solo el `.default` —`useAuthHeader` y `authHeader` corren en cada llamada a
 * la API—, MSAL renueva el access token y deja el ID token viejo. Cuando después toca la revisión
 * periódica, encuentra el access token fresco, responde desde el cache con ese ID token vencido,
 * y la app manda al usuario a autenticarse otra vez.
 *
 * Por eso van acá y no en cada sitio: basta que UNA llamada se olvide para reabrir el problema.
 */
const SCOPES_OIDC = ['openid', 'profile', 'offline_access']

// Scope de la API: el access token debe salir con aud = clientId de la API.
// Depende de getApiScope() (runtime) → función, no constante.
export const getProtectedResources = (): { apiPerseverance: { scopes: string[] } } => ({
  apiPerseverance: {
    scopes: [getApiScope(), ...SCOPES_OIDC],
  },
})

// Estático (no depende de runtime): scope fijo de Microsoft Graph.
export const userDataLoginRequest = {
  scopes: ['User.Read'],
}

// El ID token vive ~60 min; revisar cada 5 basta y evita reescribir la cookie (y
// re-renderizar a todos los suscriptores de react-cookie) una vez por minuto.
export const TOKEN_CHECK_INTERVAL_MS = 5 /* minute(s) */ * 60 * 1000

// Canal para propagar el logout al resto de pestañas del mismo origen
export const AUTH_CHANNEL = 'auth'

export const AUTH_LOGOUT_MESSAGE = 'logout'
